import LabStatusRefreshButton from "./LabStatusRefreshButton";
import React, { useEffect, useRef, useState } from "react";
import api from "../axiosConfig";
import { buildExcerptPreview } from "../utils/preview";
import { DEFAULT_TITLE_COLOR } from "../utils/titleColors";
import TitleColorField from "./TitleColorField";
import StudyGuideSlideDeck from "./StudyGuideSlideDeck";

const FOLDER_DRAG_MIME = "application/x-addanote-folder";

function formatDate(value) {
  if (!value) return "No timestamp";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No timestamp";
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
}

function collectFolderPaths(entryList) {
  const folderSet = new Set();

  for (const entry of Array.isArray(entryList) ? entryList : []) {
    const rawPath = String(entry?.folder_path || "").trim();
    if (!rawPath) continue;

    const segments = rawPath.split("/").map((segment) => segment.trim()).filter(Boolean);
    let currentPath = "";

    for (const segment of segments) {
      currentPath = currentPath ? `${currentPath}/${segment}` : segment;
      folderSet.add(currentPath);
    }
  }

  return [...folderSet].sort((left, right) => left.localeCompare(right));
}

function normalizeFolderSegment(value) {
  return String(value || "")
    .replace(/[\\/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getParentFolderPath(value) {
  const segments = String(value || "").split("/").map((segment) => segment.trim()).filter(Boolean);
  return segments.slice(0, -1).join("/");
}

function isEntryInFolder(entry, folderPath) {
  return String(entry?.folder_path || "").trim() === String(folderPath || "").trim();
}

function collectChildFolders(entryList, folderPath) {
  const currentPath = String(folderPath || "").trim();
  const childFolders = new Set();

  for (const entry of Array.isArray(entryList) ? entryList : []) {
    const rawPath = String(entry?.folder_path || "").trim();
    if (!rawPath) continue;

    const segments = rawPath.split("/").map((segment) => segment.trim()).filter(Boolean);
    const currentSegments = currentPath ? currentPath.split("/").map((segment) => segment.trim()).filter(Boolean) : [];

    const matchesCurrentPath = currentSegments.every((segment, index) => segments[index] === segment);
    if (!matchesCurrentPath) continue;
    if (segments.length <= currentSegments.length) continue;

    const nextDepth = currentSegments.length + 1;
    childFolders.add(segments.slice(0, nextDepth).join("/"));
  }

  return [...childFolders].sort((left, right) => left.localeCompare(right));
}

function getEntryFolderTarget(entry, currentFolderPath) {
  const title = String(entry?.title || "").trim();
  if (!title) return "";
  return currentFolderPath ? `${currentFolderPath}/${title}` : title;
}

function countFolderContents(entryList, folderPath) {
  return {
    noteCount: (Array.isArray(entryList) ? entryList : []).filter((entry) => isEntryInFolder(entry, folderPath)).length,
    folderCount: collectChildFolders(entryList, folderPath).length,
  };
}

function collectMoveFolderOptions(entryList, movingEntryId = "", movingFolderPath = "") {
  const excludedFolderPath = String(movingFolderPath || "").trim();
  const optionSet = new Set(collectFolderPaths(entryList));

  for (const entry of Array.isArray(entryList) ? entryList : []) {
    if (!entry?.title || entry.id === movingEntryId) continue;
    const parentPath = String(entry.folder_path || "").trim();
    const containerPath = parentPath ? `${parentPath}/${entry.title}` : String(entry.title).trim();
    if (containerPath) optionSet.add(containerPath);
  }

  return [...optionSet]
    .filter((option) => (
      !excludedFolderPath
      || (option !== excludedFolderPath && !option.startsWith(`${excludedFolderPath}/`))
    ))
    .sort((left, right) => left.localeCompare(right));
}

function parseGeneratedConfigNoteMarkup(html) {
  if (typeof html !== "string" || !html.includes("generated-config-note") || typeof DOMParser === "undefined") {
    return null;
  }

  const doc = new DOMParser().parseFromString(html, "text/html");
  const note = doc.querySelector(".generated-config-note");
  if (!note) return null;

  const meta = {};
  note.querySelectorAll(".generated-config-meta p").forEach((row) => {
    const label = row.querySelector("strong")?.textContent?.replace(/:$/, "").trim().toLowerCase();
    const value = row.textContent?.replace(row.querySelector("strong")?.textContent || "", "").trim() || "";
    if (label) meta[label] = value;
  });

  const button = note.querySelector('[data-copy-generated-config="true"]');
  const configId = note.getAttribute("data-generated-config-id")
    || button?.getAttribute("data-generated-config-id")
    || "";
  const configText = button?.getAttribute("data-generated-config")
    || note.querySelector('[data-generated-config-text="true"]')?.textContent
    || note.querySelector("pre")?.textContent
    || "";

  return {
    configId,
    generator: meta.generator || "",
    vendor: meta.vendor || "",
    saved: meta.saved || "",
    user: meta.user || "",
    configText,
  };
}

function getCopyableNoteText(note) {
  const source = String(note || "");
  if (!source.trim()) return "";
  if (typeof DOMParser === "undefined") return source;

  const doc = new DOMParser().parseFromString(source, "text/html");
  return String(doc.body?.innerText || doc.body?.textContent || source)
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function escapeHtml(value) {
  return String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatMarkdownInline(value) {
  return escapeHtml(value)
    .replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, '<img src="$2" alt="$1">')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/g, '<a href="$2">$1</a>')
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/`([^`]+)`/g, "<code>$1</code>");
}

function tableCells(line) {
  return String(line || "")
    .trim()
    .replace(/^\|/, "")
    .replace(/\|$/, "")
    .split("|")
    .map((cell) => cell.trim());
}

function markdownToClipboardHtml(markdown) {
  const lines = String(markdown || "").replace(/\r\n/g, "\n").split("\n");
  const html = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];
    if (!line.trim()) {
      index += 1;
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      const level = heading[1].length;
      html.push(`<h${level}>${formatMarkdownInline(heading[2])}</h${level}>`);
      index += 1;
      continue;
    }

    if (line.includes("|") && /^\s*\|?\s*:?-{3,}/.test(lines[index + 1] || "")) {
      const headers = tableCells(line);
      index += 2;
      const rows = [];
      while (index < lines.length && lines[index].includes("|")) {
        rows.push(tableCells(lines[index]));
        index += 1;
      }
      html.push(`<table><thead><tr>${headers.map((cell) => `<th>${formatMarkdownInline(cell)}</th>`).join("")}</tr></thead><tbody>${rows.map((row) => `<tr>${headers.map((_, cellIndex) => `<td>${formatMarkdownInline(row[cellIndex] || "")}</td>`).join("")}</tr>`).join("")}</tbody></table>`);
      continue;
    }

    if (/^\s*-\s+(?:\[[ xX]\]\s+)?/.test(line)) {
      const items = [];
      while (index < lines.length && /^\s*-\s+(?:\[[ xX]\]\s+)?/.test(lines[index])) {
        const item = lines[index].replace(/^\s*-\s+/, "");
        const checked = item.match(/^\[([ xX])\]\s+(.+)$/);
        items.push(`<li>${checked ? `${checked[1].trim() ? "☑" : "☐"} ${formatMarkdownInline(checked[2])}` : formatMarkdownInline(item)}</li>`);
        index += 1;
      }
      html.push(`<ul>${items.join("")}</ul>`);
      continue;
    }

    const paragraph = [];
    while (index < lines.length && lines[index].trim() && !/^(#{1,6})\s+/.test(lines[index]) && !/^\s*-\s+(?:\[[ xX]\]\s+)?/.test(lines[index])) {
      if (lines[index].includes("|") && /^\s*\|?\s*:?-{3,}/.test(lines[index + 1] || "")) break;
      paragraph.push(lines[index].trim());
      index += 1;
    }
    html.push(`<p>${formatMarkdownInline(paragraph.join(" "))}</p>`);
  }

  return html.join("");
}

function getCopyableNoteHtml(note) {
  const source = String(note || "");
  if (!source.trim() || typeof DOMParser === "undefined") return "";

  const doc = new DOMParser().parseFromString(source, "text/html");
  doc.querySelectorAll("script, style").forEach((element) => element.remove());
  doc.querySelectorAll("pre").forEach((element) => {
    const markdown = element.textContent || "";
    if (/^(#{1,6}\s|\s*-\s|.*\|.*\n\s*\|?\s*:?-{3,})/m.test(markdown)) {
      const rendered = doc.createElement("div");
      rendered.innerHTML = markdownToClipboardHtml(markdown);
      element.replaceWith(rendered);
    }
  });
  doc.querySelectorAll("*").forEach((element) => {
    [...element.attributes].forEach((attribute) => {
      if (/^on/i.test(attribute.name)) element.removeAttribute(attribute.name);
    });
  });
  return String(doc.body?.innerHTML || "").trim();
}

export default function NoteGroupView({
  groupId,
  learningContext = false,
  backLabel,
  onBack,
  onEditEntry,
  onCreateEntry,
  onDataChanged,
  refreshToken = 0,
  initialFolderPath = "",
  initialExpandedEntryId = "",
}) {
  const panelRef = useRef(null);
  const [group, setGroup] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [renameValue, setRenameValue] = useState("");
  const [iconValue, setIconValue] = useState("🗂️");
  const [colorValue, setColorValue] = useState(DEFAULT_TITLE_COLOR);
  const [renaming, setRenaming] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [movingEntryId, setMovingEntryId] = useState("");
  const [movingFolderPath, setMovingFolderPath] = useState("");
  const [moveTargetGroupId, setMoveTargetGroupId] = useState("");
  const [moveTargetFolderPath, setMoveTargetFolderPath] = useState("");
  const [moveFolderOptions, setMoveFolderOptions] = useState([]);
  const [loadingMoveFolders, setLoadingMoveFolders] = useState(false);
  const [currentFolderPath, setCurrentFolderPath] = useState("");
  const [backFolderHistory, setBackFolderHistory] = useState([]);
  const [forwardFolderHistory, setForwardFolderHistory] = useState([]);
  const [moving, setMoving] = useState(false);
  const [mergeTargetGroupId, setMergeTargetGroupId] = useState("");
  const [merging, setMerging] = useState(false);
  const [showMergeControls, setShowMergeControls] = useState(false);
  const [expandedEntryId, setExpandedEntryId] = useState("");
  const [entrySearch, setEntrySearch] = useState("");
  const [dragEntryId, setDragEntryId] = useState("");
  const [reordering, setReordering] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [copiedEntryId, setCopiedEntryId] = useState("");
  const [importing, setImporting] = useState(false);
  const [showTitleEditor, setShowTitleEditor] = useState(false);
  const [showTitleUtilities, setShowTitleUtilities] = useState(false);
  const [showFolderNavigation, setShowFolderNavigation] = useState(false);
  const [versionsByEntry, setVersionsByEntry] = useState({});
  const [loadingVersionsFor, setLoadingVersionsFor] = useState("");
  const [expandedHistoryEntryId, setExpandedHistoryEntryId] = useState("");
  const importInputRef = useRef(null);
  const titleColor = colorValue || group?.color || DEFAULT_TITLE_COLOR;

  const openCreateEntry = () => onCreateEntry(group, currentFolderPath);
  const openCreateEntryInFolder = (folderPath) => onCreateEntry(group, folderPath);

  const loadGroup = async (activeGroupId) => {
    const res = await api.get(`/api/notes/${activeGroupId}`);
    setGroup(res.data.group || null);
    setEntries(res.data.entries || []);
    setRenameValue(res.data.group?.title || "");
    setIconValue(res.data.group?.icon || "🗂️");
    setColorValue(res.data.group?.color || DEFAULT_TITLE_COLOR);
  };

  const copyShareToClipboard = async (url) => {
    if (!url) return;
    await navigator.clipboard.writeText(url);
    setShareCopied(true);
    window.setTimeout(() => setShareCopied(false), 1800);
  };

  const handleCopyNote = async (entry) => {
    const text = getCopyableNoteText(entry?.note);
    const html = getCopyableNoteHtml(entry?.note);
    if (!text) {
      setError("This note has no text to copy.");
      return;
    }

    try {
      if (navigator.clipboard?.write && typeof ClipboardItem !== "undefined" && html) {
        await navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([html], { type: "text/html" }),
            "text/plain": new Blob([text], { type: "text/plain" }),
          }),
        ]);
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const fallbackArea = document.createElement("div");
        fallbackArea.contentEditable = "true";
        fallbackArea.style.position = "fixed";
        fallbackArea.style.left = "-9999px";
        fallbackArea.style.top = "0";
        fallbackArea.innerHTML = html || text.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>");
        document.body.appendChild(fallbackArea);
        const selection = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(fallbackArea);
        selection?.removeAllRanges();
        selection?.addRange(range);
        document.execCommand("copy");
        selection?.removeAllRanges();
        document.body.removeChild(fallbackArea);
      }
      setCopiedEntryId(entry.id);
      window.setTimeout(() => setCopiedEntryId((current) => (current === entry.id ? "" : current)), 1500);
    } catch {
      setError("Copy failed. Select the note text and copy it manually.");
    }
  };

  const handleGeneratedConfigCopy = async (event) => {
    const button = event?.currentTarget instanceof HTMLButtonElement
      ? event.currentTarget
      : (event?.target instanceof Element ? event.target.closest?.('[data-copy-generated-config="true"]') : null);
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();

    const text = button.getAttribute("data-generated-config") || "";
    const originalLabel = button.textContent || "Copy config";
    if (!text.trim()) {
      button.textContent = "No config";
      window.setTimeout(() => {
        button.textContent = originalLabel;
      }, 1500);
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const fallbackArea = document.createElement("textarea");
        fallbackArea.value = text;
        fallbackArea.setAttribute("readonly", "true");
        fallbackArea.style.position = "fixed";
        fallbackArea.style.opacity = "0";
        document.body.appendChild(fallbackArea);
        fallbackArea.select();
        document.execCommand("copy");
        document.body.removeChild(fallbackArea);
      }
      button.textContent = "Copied";
      window.setTimeout(() => {
        button.textContent = originalLabel;
      }, 1500);
    } catch {
      button.textContent = "Copy failed";
      window.setTimeout(() => {
        button.textContent = "Copy config";
      }, 1500);
    }
  };

  const handleDeleteGeneratedConfig = async (entry, parsedConfig) => {
    if (!entry?.id || !parsedConfig) return;
    if (!window.confirm("Delete this saved configuration block?")) return;

    try {
      const parser = typeof DOMParser !== "undefined" ? new DOMParser() : null;
      if (!parser) {
        setError("Browser parsing is not available for deleting this config.");
        return;
      }

      const doc = parser.parseFromString(String(entry.note || ""), "text/html");
      const blocks = Array.from(doc.querySelectorAll(".generated-config-note"));
      const targetBlock = blocks.find((block) => {
        const blockId = block.getAttribute("data-generated-config-id")
          || block.querySelector('[data-generated-config-id]')?.getAttribute("data-generated-config-id")
          || "";
        const blockText = block.querySelector('[data-generated-config-text="true"]')?.textContent
          || block.querySelector("pre")?.textContent
          || block.querySelector('[data-copy-generated-config="true"]')?.getAttribute("data-generated-config")
          || "";

        if (parsedConfig.configId && blockId) {
          return blockId === parsedConfig.configId;
        }
        return blockText.trim() === String(parsedConfig.configText || "").trim();
      });

      if (!targetBlock) {
        setError("Could not find that saved configuration block.");
        return;
      }

      targetBlock.remove();
      const remainingMarkup = String(doc.body?.innerHTML || "").trim();
      const hasMeaningfulContent = Boolean(
        remainingMarkup
          .replace(/<(br|hr)\s*\/?>/gi, "")
          .replace(/<(p|div|section|article|header|footer|main|span|strong|em|b|i)\b[^>]*>\s*<\/\1>/gi, "")
          .replace(/&nbsp;/gi, "")
          .replace(/\s+/g, "")
      );

      if (!hasMeaningfulContent) {
        await api.delete(`/api/note-entries/${entry.id}`);
      } else {
        await api.put(`/api/note-entries/${entry.id}`, {
          title: entry.title,
          note: remainingMarkup,
          status: entry.status || "active",
          tags: Array.isArray(entry.tags) ? entry.tags : [],
          folderPath: entry.folder_path || "",
        });
      }

      await loadGroup(groupId);
      onDataChanged?.([groupId]);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete saved configuration.");
    }
  };

  const renderGeneratedConfigNote = (entry) => {
    const parsed = parseGeneratedConfigNoteMarkup(entry?.note);
    if (!parsed) return null;

    return (
      <section className="generated-config-note" data-generator-note="true">
        <div className="generated-config-meta">
          <p><strong>Generator:</strong> {parsed.generator}</p>
          <p><strong>Vendor:</strong> {parsed.vendor}</p>
          <p><strong>Saved:</strong> {parsed.saved}</p>
          <p><strong>User:</strong> {parsed.user}</p>
        </div>
        <div className="generated-config-toolbar">
          <button
            type="button"
            className="generated-config-delete-button"
            onClick={() => handleDeleteGeneratedConfig(entry, parsed)}
          >
            Delete config
          </button>
          <button
            type="button"
            className="generated-config-copy-button"
            data-copy-generated-config="true"
            data-generated-config-id={parsed.configId}
            data-generated-config={parsed.configText}
            onClick={handleGeneratedConfigCopy}
          >
            Copy config
          </button>
        </div>
        <pre data-generated-config-text="true"><code>{parsed.configText}</code></pre>
      </section>
    );
  };

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;

    const handlePanelClick = (event) => {
      const targetNode = event.target;
      const targetElement = targetNode instanceof Element ? targetNode : targetNode?.parentElement || null;
      if (!targetElement?.closest?.('[data-copy-generated-config="true"]')) return;
      handleGeneratedConfigCopy(event);
    };

    panel.addEventListener("click", handlePanelClick);
    return () => {
      panel.removeEventListener("click", handlePanelClick);
    };
  }, []);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return undefined;

    const buttons = Array.from(panel.querySelectorAll('[data-copy-generated-config="true"]'));
    if (!buttons.length) return undefined;

    const handleButtonClick = (event) => {
      handleGeneratedConfigCopy(event);
    };

    buttons.forEach((button) => {
      button.addEventListener("click", handleButtonClick);
    });

    return () => {
      buttons.forEach((button) => {
        button.removeEventListener("click", handleButtonClick);
      });
    };
  }, [entries, expandedEntryId]);

  const renderEntryHTML = (html) => {
    if (typeof html !== "string") return { __html: "" };
    const documentStripped = html
      .replace(/<!doctype[^>]*>/gi, "")
      .replace(/<html[^>]*>/gi, "")
      .replace(/<\/html>/gi, "")
      .replace(/<head[\s\S]*?<\/head>/gi, "")
      .replace(/<\/?(body|main|header|footer)[^>]*>/gi, "");
    const commentsVisible = documentStripped.replace(
      /<!--([\s\S]*?)-->/g,
      (_match, comment) =>
        `<div class="html-comment-note">HTML Comment: ${String(comment).trim() || "Hidden source code text"}</div>`
    );
    const noScripts = commentsVisible
      .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, "")
      .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, "");
    const noHandlers = noScripts
      .replace(/on\w+\s*=\s*"[^"]*"/gi, "")
      .replace(/on\w+\s*=\s*'[^']*'/gi, "");
    const normalizedBackgrounds = noHandlers.replace(
      /style\s*=\s*"([^"]*)"/gi,
      (_match, styleValue) => {
        const cleaned = String(styleValue)
          .split(";")
          .map((part) => part.trim())
          .filter(Boolean)
          .filter((part) => !/^background(?:-color)?\s*:/i.test(part))
          .join("; ");
        return cleaned ? `style="${cleaned}"` : "";
      }
    );
    const urlPattern = /(^|[^"'=])(https?:\/\/[^\s<]+)/g;
    const withLinks = normalizedBackgrounds.replace(
      urlPattern,
      (_match, prefix, url) => `${prefix}<a href="${url}" target="_blank" rel="noopener noreferrer">${url}</a>`
    );
    const withImageFallbacks = withLinks.replace(/<img\b([^>]*)>/gi, (_match, attributes) => {
      const attributeText = String(attributes || "");
      const classMatch = attributeText.match(/\sclass=(["'])(.*?)\1/i);
      const className = classMatch?.[2] ? `${classMatch[2]} note-rendered-image` : "note-rendered-image";
      const cleanedAttributes = attributeText.replace(/\sclass=(["'])(.*?)\1/i, "").trim();
      return `<img ${cleanedAttributes} class="${className}" onerror="this.style.display='none';var p=this.parentElement;if(p&&!p.querySelector('.missing-image-label')){p.classList.add('is-missing-image');var c=document.createElement('div');c.className='missing-image-label';c.textContent='Missing image: ' + (this.getAttribute('alt') || 'Attachment');p.appendChild(c);}" />`;
    });
    return { __html: withImageFallbacks };
  };

  const getEntryPreview = (html) => {
    const preview = buildExcerptPreview(html, 140).trim();
    if (!preview) return "Open to view this note.";
    return preview.length >= 140 ? `${preview}...` : preview;
  };

  const filteredEntries = entries.filter((entry) => {
    if (!isEntryInFolder(entry, currentFolderPath)) return false;

    const query = entrySearch.trim().toLowerCase();
    if (!query) return true;
    const haystack = [
      entry.title,
      entry.folder_path,
      entry.note,
      entry.status,
      ...(Array.isArray(entry.tags) ? entry.tags : []),
    ]
      .join(" ")
      .toLowerCase();
    return haystack.includes(query);
  });
  const childFolders = collectChildFolders(entries, currentFolderPath).filter((folderPath) => {
    const query = entrySearch.trim().toLowerCase();
    if (!query) return true;
    return folderPath.toLowerCase().includes(query);
  });
  const visibleFolderPaths = childFolders.filter((folderPath) => (
    !filteredEntries.some((entry) => getEntryFolderTarget(entry, currentFolderPath) === folderPath)
  ));
  const mergeGroupOptions = groupOptions.filter((item) => item.id !== groupId);

  useEffect(() => {
    if (!groupId) return;

    let cancelled = false;
    const loadGroupData = async () => {
      setLoading(true);
      setError("");
      try {
        const [groupRes, titlesRes] = await Promise.all([
          api.get(`/api/notes/${groupId}`),
          api.get("/api/notes?limit=100&offset=0"),
        ]);
        if (initialExpandedEntryId && !(groupRes.data.entries || []).some(e=>String(e.id)===String(initialExpandedEntryId))) {
          const moved=await api.get(`/api/note-entries/${initialExpandedEntryId}`);
          if (!cancelled && moved.data.learning_course_id && moved.data.group_id!==groupId) {
            window.location.replace(`/learning/course/${encodeURIComponent(moved.data.learning_course_id)}?note=${encodeURIComponent(initialExpandedEntryId)}`);
            return;
          }
          if (!cancelled && moved.data.learning_program_id && moved.data.group_id!==groupId) {
            window.location.replace(`/learning/program/${encodeURIComponent(moved.data.learning_program_id)}?note=${encodeURIComponent(initialExpandedEntryId)}`);
            return;
          }
        }
        if (!cancelled) {
          setGroup(groupRes.data.group || null);
          setEntries(groupRes.data.entries || []);
          setRenameValue(groupRes.data.group?.title || "");
          setIconValue(groupRes.data.group?.icon || "🗂️");
          setColorValue(groupRes.data.group?.color || DEFAULT_TITLE_COLOR);
          setGroupOptions(titlesRes.data.items || []);
          setShowMergeControls(false);
          setMergeTargetGroupId("");
          setExpandedEntryId("");
          setEntrySearch("");
          setDragEntryId("");
          setMoveTargetGroupId("");
          setMoveTargetFolderPath("");
          setMoveFolderOptions([]);
          const initialEntry = (groupRes.data.entries || []).find((entry) => (
            String(entry.id) === String(initialExpandedEntryId || "")
          ));
          setCurrentFolderPath(String(initialFolderPath || initialEntry?.folder_path || "").trim());
          setBackFolderHistory([]);
          setForwardFolderHistory([]);
          setVersionsByEntry({});
          setExpandedHistoryEntryId("");
          setExpandedEntryId(String(initialExpandedEntryId || "").trim());
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || "Failed to load title.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadGroupData();

    return () => {
      cancelled = true;
    };
  // Folder and entry selection are handled by the focused effects below. They
  // must not refetch the whole title, which can race with the post-save refresh.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupId, refreshToken]);

  useEffect(() => {
    setCurrentFolderPath(String(initialFolderPath || "").trim());
    setBackFolderHistory([]);
    setForwardFolderHistory([]);
  }, [initialFolderPath, groupId]);

  useEffect(() => {
    setExpandedEntryId(String(initialExpandedEntryId || "").trim());
  }, [initialExpandedEntryId, groupId]);

  const handleRename = async (event) => {
    event.preventDefault();
    const trimmed = renameValue.trim();
    if (!trimmed || !group) return;
    setRenaming(true);
    setError("");
    try {
      const res = await api.put(`/api/notes/${group.id}`, { title: trimmed, icon: iconValue, color: colorValue });
      setGroup((current) => ({ ...current, ...res.data }));
      onDataChanged?.([group.id]);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to rename title.");
    } finally {
      setRenaming(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm("Delete this note?")) return;
    try {
      await api.delete(`/api/note-entries/${entryId}`);
      await loadGroup(groupId);
      onDataChanged?.([groupId]);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to delete note.");
    }
  };

  const handleMergeGroup = async () => {
    if (!mergeTargetGroupId || !group) return;
    if (!window.confirm("Copy all notes in this title into the selected title? The current title will stay where it is.")) {
      return;
    }

    setMerging(true);
    setError("");
    try {
      await api.post(`/api/notes/${group.id}/merge`, {
        targetGroupId: mergeTargetGroupId,
      });
      setMergeTargetGroupId("");
      setShowMergeControls(false);
      onDataChanged?.([group.id, mergeTargetGroupId]);
      await loadGroup(groupId);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to copy title into destination.");
    } finally {
      setMerging(false);
    }
  };

  const loadMoveFolders = async (targetId, entryId = "", folderPath = "") => {
    if (!targetId) {
      setMoveFolderOptions([]);
      return;
    }

    setLoadingMoveFolders(true);
    try {
      const res = await api.get(`/api/notes/${targetId}`);
      setMoveFolderOptions(collectMoveFolderOptions(
        res.data.entries || [],
        targetId === groupId ? entryId : "",
        targetId === groupId ? folderPath : ""
      ));
    } catch (_err) {
      setMoveFolderOptions([]);
    } finally {
      setLoadingMoveFolders(false);
    }
  };

  const addMoveFolderOption = (nextPath) => {
    if (!nextPath) return;
    setMoveFolderOptions((current) => [...new Set([...current, nextPath])].sort((left, right) => left.localeCompare(right)));
  };

  const handleCreateMoveFolder = () => {
    const folderName = normalizeFolderSegment(
      window.prompt(
        moveTargetFolderPath
          ? `Enter the name of the new sub-folder inside "${moveTargetFolderPath}"`
          : "Enter the name of the new top-level folder",
        ""
      )
    );

    if (!folderName) return;

    const nextPath = moveTargetFolderPath ? `${moveTargetFolderPath}/${folderName}` : folderName;
    setMoveTargetFolderPath(nextPath);
    addMoveFolderOption(nextPath);
  };

  const handleCreateFolderHere = () => {
    const folderName = normalizeFolderSegment(
      window.prompt(
        currentFolderPath
          ? `Enter the name of the new sub-folder inside "${currentFolderPath}"`
          : "Enter the name of the new top-level folder",
        ""
      )
    );

    if (!folderName) return;

    const nextFolderPath = currentFolderPath ? `${currentFolderPath}/${folderName}` : folderName;
    openCreateEntryInFolder(nextFolderPath);
  };

  const openFolder = (nextFolderPath) => {
    const normalizedPath = String(nextFolderPath || "").trim();
    if (normalizedPath === currentFolderPath) return;
    setBackFolderHistory((current) => [...current, currentFolderPath]);
    setForwardFolderHistory([]);
    setCurrentFolderPath(normalizedPath);
    setExpandedEntryId("");
  };

  const handleFolderBack = () => {
    if (!backFolderHistory.length) return;
    const previousFolderPath = backFolderHistory[backFolderHistory.length - 1];
    setBackFolderHistory((current) => current.slice(0, -1));
    setForwardFolderHistory((current) => [currentFolderPath, ...current]);
    setCurrentFolderPath(previousFolderPath);
    setExpandedEntryId("");
  };

  const handleFolderForward = () => {
    if (!forwardFolderHistory.length) return;
    const [nextFolderPath, ...remaining] = forwardFolderHistory;
    setForwardFolderHistory(remaining);
    setBackFolderHistory((current) => [...current, currentFolderPath]);
    setCurrentFolderPath(nextFolderPath);
    setExpandedEntryId("");
  };

  const handleMoveEntry = async (entryId) => {
    if (!moveTargetGroupId) return;
    setMoving(true);
    setError("");
    try {
      const res = await api.post(`/api/note-entries/${entryId}/move`, {
        targetGroupId: moveTargetGroupId,
        targetFolderPath: moveTargetFolderPath,
      });

      setMovingEntryId("");
      setMoveTargetGroupId("");
      setMoveTargetFolderPath("");
      setMoveFolderOptions([]);
      onDataChanged?.([groupId, moveTargetGroupId]);

      if (res.data?.sourceDeleted) {
        onBack?.();
        return;
      }

      await loadGroup(groupId);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to move note.");
    } finally {
      setMoving(false);
    }
  };

  const handleMoveFolder = async (folderPath) => {
    if (!moveTargetGroupId || !folderPath) return;
    setMoving(true);
    setError("");
    try {
      const res = await api.post(`/api/notes/${groupId}/folders/move`, {
        sourceFolderPath: folderPath,
        targetGroupId: moveTargetGroupId,
        targetFolderPath: moveTargetFolderPath,
      });

      setMovingFolderPath("");
      setMoveTargetGroupId("");
      setMoveTargetFolderPath("");
      setMoveFolderOptions([]);
      onDataChanged?.([groupId, moveTargetGroupId]);

      if (res.data?.sourceDeleted) {
        onBack?.();
        return;
      }

      const nextCurrentFolder = currentFolderPath === folderPath || currentFolderPath.startsWith(`${folderPath}/`)
        ? ""
        : currentFolderPath;
      if (nextCurrentFolder !== currentFolderPath) {
        setCurrentFolderPath(nextCurrentFolder);
        setBackFolderHistory([]);
        setForwardFolderHistory([]);
      }

      await loadGroup(groupId);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to move folder.");
    } finally {
      setMoving(false);
    }
  };

  const handleExportGroup = async () => {
    try {
      const res = await api.get(`/api/notes/${groupId}/export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${(group?.title || "title").replace(/[^a-z0-9-_]+/gi, "-").toLowerCase()}.json`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to export title.");
    }
  };

  const handleImportGroup = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setError("");
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      const entries = Array.isArray(parsed?.entries) ? parsed.entries : Array.isArray(parsed) ? parsed : [];
      if (!entries.length) {
        throw new Error("Import file does not contain any notes.");
      }
      await api.post(`/api/notes/${groupId}/import`, { entries });
      await loadGroup(groupId);
      onDataChanged?.([groupId]);
    } catch (err) {
      setError(err.response?.data?.error || err.message || "Failed to import title.");
    } finally {
      event.target.value = "";
      setImporting(false);
    }
  };

  const handleCreateShare = async () => {
    setShareLoading(true);
    setError("");
    try {
      const res = await api.post(`/api/notes/${groupId}/share`);
      const absolute = `${window.location.origin}${res.data.url}`;
      setShareUrl(absolute);
      await copyShareToClipboard(absolute);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to create share link.");
    } finally {
      setShareLoading(false);
    }
  };

  const handleRevokeShare = async () => {
    setShareLoading(true);
    setError("");
    try {
      await api.delete(`/api/notes/${groupId}/share`);
      setShareUrl("");
      setShareCopied(false);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to revoke share link.");
    } finally {
      setShareLoading(false);
    }
  };

  const loadVersions = async (entryId) => {
    if (versionsByEntry[entryId]) return;
    setLoadingVersionsFor(entryId);
    try {
      const res = await api.get(`/api/note-entries/${entryId}/versions`);
      setVersionsByEntry((current) => ({ ...current, [entryId]: res.data.items || [] }));
    } catch (_err) {
      setVersionsByEntry((current) => ({ ...current, [entryId]: [] }));
    } finally {
      setLoadingVersionsFor("");
    }
  };

  const handleToggleExpand = async (entryId) => {
    const nextId = expandedEntryId === entryId ? "" : entryId;
    setExpandedEntryId(nextId);
    if (nextId !== entryId && expandedHistoryEntryId === entryId) {
      setExpandedHistoryEntryId("");
    }
  };

  const handleToggleHistory = async (entryId) => {
    const nextId = expandedHistoryEntryId === entryId ? "" : entryId;
    setExpandedHistoryEntryId(nextId);
    if (nextId) {
      await loadVersions(entryId);
    }
  };

  const handleRestoreVersion = async (entryId, versionId) => {
    if (!window.confirm("Restore this note to the selected version?")) return;
    try {
      await api.post(`/api/note-entries/${entryId}/restore/${versionId}`);
      await loadGroup(groupId);
      setVersionsByEntry((current) => {
        const next = { ...current };
        delete next[entryId];
        return next;
      });
      await loadVersions(entryId);
      onDataChanged?.([groupId]);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to restore version.");
    }
  };

  const persistEntryOrder = async (nextEntries) => {
    setEntries(nextEntries);
    setReordering(true);
    setError("");
    try {
      await api.put(`/api/notes/${groupId}/entries/reorder`, {
        entryIds: nextEntries.map((entry) => entry.id),
      });
      onDataChanged?.([groupId]);
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save note order.");
      await loadGroup(groupId);
    } finally {
      setReordering(false);
    }
  };

  const handleDropEntry = async (targetEntryId) => {
    if (!dragEntryId || dragEntryId === targetEntryId) {
      setDragEntryId("");
      return;
    }

    const currentEntries = [...entries];
    const fromIndex = currentEntries.findIndex((entry) => entry.id === dragEntryId);
    const toIndex = currentEntries.findIndex((entry) => entry.id === targetEntryId);
    if (fromIndex === -1 || toIndex === -1) {
      setDragEntryId("");
      return;
    }

    const [movedEntry] = currentEntries.splice(fromIndex, 1);
    currentEntries.splice(toIndex, 0, movedEntry);
    setDragEntryId("");
    await persistEntryOrder(currentEntries);
  };

  const handleFolderDragStart = (event, folderPath) => {
    const payload = JSON.stringify({
      type: "folder",
      sourceGroupId: groupId,
      sourceFolderPath: folderPath,
    });
    event.dataTransfer?.setData(FOLDER_DRAG_MIME, payload);
    event.dataTransfer?.setData("text/plain", payload);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = "move";
    }
  };

  if (loading) return <div className="feedback-panel">Loading title...</div>;
  if (error && !group) return <div className="feedback-panel error">{error}</div>;
  if (!group) return <div className="feedback-panel">Title not found.</div>;

  return (
    <section className="group-panel" ref={panelRef}>
      <div className="group-header">
        <button className="action-link" onClick={onBack}>{backLabel || (learningContext ? "Back to program folders" : "Back to titles")}</button>
        <div className="group-header-actions">
          <button className="action-button" onClick={handleCreateFolderHere}>
            {currentFolderPath ? "Create folder here" : "Create root folder"}
          </button>
          <button className="action-button action-button-secondary" onClick={openCreateEntry}>
            {currentFolderPath ? "Add note here" : "Add note inside title"}
          </button>
        </div>
      </div>

      <div className="group-summary">
        <div>
          <span className="eyebrow">{learningContext ? "Learning folder" : "Title"}</span>
          <h2 style={{ color: titleColor }}><span>{group.icon || "🗂️"}</span> {group.title}</h2>
          <p>{entries.length} note{entries.length === 1 ? "" : "s"} inside this {learningContext ? "folder" : "title"}.</p>
        </div>
        <div className="group-summary-meta">
          <span className="group-color-chip"><span style={{ background: titleColor }} /> {titleColor}</span>
          <span>Created {formatDate(group.created_at)}</span>
          <span>Updated {formatDate(group.updated_at)}</span>
        </div>
      </div>

      <section className={`group-collapsible-card ${showTitleEditor ? "is-open" : ""}`}>
        <button
          type="button"
          className="group-collapsible-toggle"
          onClick={() => setShowTitleEditor((current) => !current)}
          aria-expanded={showTitleEditor}
        >
          <span>
            <strong>Edit title</strong>
            <small>Rename, change icon, or update color.</small>
          </span>
          <span className="group-collapsible-indicator">{showTitleEditor ? "Hide" : "Show"}</span>
        </button>

        {showTitleEditor ? (
          <form className="group-rename-form" onSubmit={handleRename}>
            <label className="editor-field">
              <span>Rename title</span>
              <input
                value={renameValue}
                onChange={(event) => setRenameValue(event.target.value)}
                placeholder="Title name"
              />
            </label>
            <label className="editor-field">
              <span>Icon</span>
              <input value={iconValue} onChange={(event) => setIconValue(event.target.value)} placeholder="🗂️" />
            </label>
            <TitleColorField label="Color" value={colorValue} onChange={setColorValue} />
            <button className="action-button action-button-secondary" type="submit" disabled={renaming}>
              {renaming ? "Saving..." : "Update title"}
            </button>
          </form>
        ) : null}
      </section>

      <section className={`group-collapsible-card ${showTitleUtilities ? "is-open" : ""}`}>
        <button
          type="button"
          className="group-collapsible-toggle"
          onClick={() => setShowTitleUtilities((current) => !current)}
          aria-expanded={showTitleUtilities}
        >
          <span>
            <strong>Title tools</strong>
            <small>Export, import, and share this title.</small>
          </span>
          <span className="group-collapsible-indicator">{showTitleUtilities ? "Hide" : "Show"}</span>
        </button>

        {showTitleUtilities ? (
          <>
            <div className="group-utility-row">
              <button className="action-button action-button-secondary" onClick={handleExportGroup}>Export title</button>
              <button className="action-button action-button-secondary" onClick={() => importInputRef.current?.click()} disabled={importing}>
                {importing ? "Importing..." : "Import title"}
              </button>
              <input ref={importInputRef} type="file" accept="application/json" onChange={handleImportGroup} className="visually-hidden-input" />
              <button className="action-button action-button-secondary" onClick={handleCreateShare} disabled={shareLoading}>
                {shareLoading ? "Working..." : "Create share link"}
              </button>
              {shareUrl ? <button className="action-button action-button-secondary" onClick={() => copyShareToClipboard(shareUrl)}>Copy link</button> : null}
              {shareUrl ? <button className="action-link" onClick={handleRevokeShare}>Revoke share</button> : null}
            </div>
            {shareUrl ? (
              <div className="group-share-box">
                <div className="group-share-header">
                  <strong>Read-only link</strong>
                  {shareCopied ? <span className="group-share-status">Copied</span> : null}
                </div>
                <span>{shareUrl}</span>
              </div>
            ) : null}
          </>
        ) : null}
      </section>

      {mergeGroupOptions.length ? (
        showMergeControls ? (
          <div className="group-merge-form">
            <label className="editor-field">
              <span>Copy this title into another title</span>
              <select
                value={mergeTargetGroupId}
                onChange={(event) => setMergeTargetGroupId(event.target.value)}
              >
                <option value="">Choose destination title</option>
                {mergeGroupOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.title}
                  </option>
                ))}
              </select>
            </label>
            <div className="group-move-actions">
              <button
                className="action-button action-button-secondary"
                onClick={handleMergeGroup}
                disabled={merging || !mergeTargetGroupId}
              >
                {merging ? "Copying..." : "Copy into selected title"}
              </button>
              <button
                className="action-link"
                onClick={() => {
                  setShowMergeControls(false);
                  setMergeTargetGroupId("");
                }}
                disabled={merging}
              >
                Cancel
              </button>
              <small>All notes from this title will be copied to the destination title, and this title will be preserved.</small>
            </div>
          </div>
        ) : (
          <div className="group-merge-trigger">
            <button
              className="action-link"
              onClick={() => setShowMergeControls(true)}
            >
              Copy title
            </button>
          </div>
        )
      ) : null}

      {error ? <div className="feedback-panel error">{error}</div> : null}

      <section className={`group-collapsible-card ${showFolderNavigation ? "is-open" : ""}`}>
        <button
          type="button"
          className="group-collapsible-toggle"
          onClick={() => setShowFolderNavigation((current) => !current)}
          aria-expanded={showFolderNavigation}
        >
          <span>
            <strong>Folder navigation</strong>
            <small>Back, forward, and current folder location.</small>
          </span>
          <span className="group-collapsible-indicator">{showFolderNavigation ? "Hide" : "Show"}</span>
        </button>

        {showFolderNavigation ? (
          <div className="folder-nav-bar">
            <div className="folder-nav-actions">
              <button
                className="action-button action-button-secondary"
                onClick={handleFolderBack}
                disabled={!backFolderHistory.length}
              >
                Back folder
              </button>
              <button
                className="action-button action-button-secondary"
                onClick={handleFolderForward}
                disabled={!forwardFolderHistory.length}
              >
                Forward folder
              </button>
            </div>
            <div className="folder-nav-location">
              <span className="eyebrow">Current folder</span>
              <strong>{currentFolderPath || "Top level"}</strong>
              {currentFolderPath ? (
                <button
                  className="action-link"
                  onClick={() => openFolder(getParentFolderPath(currentFolderPath))}
                >
                  Go up
                </button>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      <div className="group-toolbar">
        <label className="editor-field group-search-field">
          <span>Search inside this title</span>
          <input
            type="search"
            value={entrySearch}
            onChange={(event) => setEntrySearch(event.target.value)}
            placeholder="Filter by note title, content, status, or tag"
          />
        </label>
        {entrySearch ? (
          <button
            className="action-link"
            onClick={() => setEntrySearch("")}
          >
            Clear filter
          </button>
        ) : null}
        {reordering ? <div className="saving-badge">Saving order...</div> : null}
      </div>

      {!entries.length ? (
        <div className="empty-state">
          <h3>No notes inside this title yet</h3>
          <p>
            {currentFolderPath
              ? "Create the first note or sub-folder inside this location."
              : "Create the first note entry or root folder for this title."}
          </p>
          <div className="group-header-actions">
            <button className="action-button action-button-secondary" onClick={handleCreateFolderHere}>
              {currentFolderPath ? "Create folder here" : "Create root folder"}
            </button>
            <button className="action-button" onClick={openCreateEntry}>
              {currentFolderPath ? "Add first note here" : "Add first note"}
            </button>
          </div>
        </div>
      ) : !visibleFolderPaths.length && !filteredEntries.length ? (
        <div className="empty-state">
          <h3>No matching notes</h3>
          <p>Try a broader search term or clear the filter.</p>
        </div>
      ) : (
        <>
          {visibleFolderPaths.length ? (
            <div className="folder-grid">
              {visibleFolderPaths.map((folderPath) => {
                const label = folderPath.split("/").filter(Boolean).pop() || folderPath;
                const counts = countFolderContents(entries, folderPath);
                return (
                  <div
                    key={folderPath}
                    className="folder-card"
                    draggable
                    onDragStart={(event) => handleFolderDragStart(event, folderPath)}
                  >
                    <span className="folder-card-icon" aria-hidden="true">📁</span>
                    <strong>{label}</strong>
                    <small>{folderPath}</small>
                    <div className="folder-card-counts">
                      <span>{counts.noteCount} note{counts.noteCount === 1 ? "" : "s"}</span>
                      <span>{counts.folderCount} folder{counts.folderCount === 1 ? "" : "s"}</span>
                    </div>
                    <div className="folder-card-actions">
                      <button
                        type="button"
                        className="action-link"
                        onClick={() => openFolder(folderPath)}
                      >
                        Open
                      </button>
                      <button
                        type="button"
                        className="action-link"
                        onClick={() => {
                          setMovingEntryId("");
                          setMovingFolderPath((current) => (current === folderPath ? "" : folderPath));
                          setMoveTargetGroupId("");
                          setMoveTargetFolderPath("");
                          setMoveFolderOptions([]);
                        }}
                      >
                        Move
                      </button>
                    </div>
                    {movingFolderPath === folderPath ? (
                      <div className="group-move-form folder-card-move-form">
                        <label className="editor-field">
                          <span>Move folder to title</span>
                          <select
                            value={moveTargetGroupId}
                            onChange={async (event) => {
                              const nextGroupId = event.target.value;
                              setMoveTargetGroupId(nextGroupId);
                              setMoveTargetFolderPath("");
                              await loadMoveFolders(nextGroupId, "", folderPath);
                            }}
                          >
                            <option value="">Choose destination title</option>
                            {groupOptions.map((option) => (
                              <option key={option.id} value={option.id}>
                                {option.id === groupId ? `${option.title} (current title)` : option.title}
                              </option>
                            ))}
                          </select>
                        </label>
                        {moveTargetGroupId ? (
                          <>
                            <label className="editor-field">
                              <span>Destination parent folder</span>
                              <select
                                value={moveTargetFolderPath}
                                onChange={(event) => setMoveTargetFolderPath(event.target.value)}
                                disabled={loadingMoveFolders}
                              >
                                <option value="">
                                  {loadingMoveFolders ? "Loading folders..." : "Top level of destination title"}
                                </option>
                                {moveFolderOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <div className="editor-field">
                              <span>Folder move rules</span>
                              <div className="folder-picker-card">
                                <strong>{moveTargetFolderPath || "Top level of destination title"}</strong>
                                <p>
                                  The folder name stays the same. This moves
                                  {" "}
                                  <strong>{folderPath.split("/").filter(Boolean).pop() || folderPath}</strong>
                                  {" "}
                                  under the selected destination parent.
                                </p>
                                <div className="folder-picker-actions">
                                  <button
                                    type="button"
                                    className="action-button action-button-secondary"
                                    onClick={handleCreateMoveFolder}
                                    disabled={loadingMoveFolders}
                                  >
                                    {moveTargetFolderPath ? "Create sub-folder here" : "Create root folder"}
                                  </button>
                                  {moveTargetFolderPath ? (
                                    <button
                                      type="button"
                                      className="action-link"
                                      onClick={() => setMoveTargetFolderPath("")}
                                      disabled={loadingMoveFolders}
                                    >
                                      Return to top level
                                    </button>
                                  ) : null}
                                </div>
                              </div>
                            </div>
                            <div className="group-move-actions">
                              <button
                                className="action-button action-button-secondary"
                                onClick={() => handleMoveFolder(folderPath)}
                                disabled={moving || !moveTargetGroupId}
                              >
                                {moving ? "Moving..." : "Move folder"}
                              </button>
                              <button
                                className="action-link"
                                onClick={() => {
                                  setMovingFolderPath("");
                                  setMoveTargetGroupId("");
                                  setMoveTargetFolderPath("");
                                  setMoveFolderOptions([]);
                                }}
                                disabled={moving}
                              >
                                Cancel
                              </button>
                            </div>
                          </>
                        ) : null}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          ) : null}
          <ol className="group-entries">
            {filteredEntries.map((entry, index) => {
              const openFolderPath = getEntryFolderTarget(entry, currentFolderPath);
              const canOpenFolder = childFolders.includes(openFolderPath);
              const nestedCounts = canOpenFolder ? countFolderContents(entries, openFolderPath) : null;

              return (
            <li
              className={`group-entry-card ${dragEntryId === entry.id ? "is-dragging" : ""}`}
              key={entry.id}
              draggable
              onDragStart={() => setDragEntryId(entry.id)}
              onDragOver={(event) => event.preventDefault()}
              onDrop={() => handleDropEntry(entry.id)}
              onDragEnd={() => setDragEntryId("")}
            >
              <div className="group-entry-index" aria-hidden="true">
                <span>{String(index + 1).padStart(2, "0")}</span>
                <span className="group-entry-handle" title="Drag to reorder">::</span>
              </div>
              <div className="group-entry-top">
                <div>
                  <div className="group-entry-title-row">
                    <button
                      type="button"
                      className="group-entry-title-button"
                      onClick={() => handleToggleExpand(entry.id)}
                      aria-expanded={expandedEntryId === entry.id}
                    >
                      <h3>{entry.title}</h3>
                    </button>
                    <span className={`entry-status-badge is-${entry.status || "active"}`}>{entry.status || "active"}</span>
                  </div>
                  <div className="group-entry-meta">
                    <span>{entry.author || "You"}</span>
                    <span>{formatDate(entry.updated_at || entry.created_at)}</span>
                    {entry.folder_path ? <span>{entry.folder_path}</span> : null}
                  </div>
                  {nestedCounts ? (
                    <div className="group-entry-counts">
                      <span>{nestedCounts.noteCount} note{nestedCounts.noteCount === 1 ? "" : "s"}</span>
                      <span>{nestedCounts.folderCount} folder{nestedCounts.folderCount === 1 ? "" : "s"}</span>
                    </div>
                  ) : null}
                  {Array.isArray(entry.tags) && entry.tags.length ? (
                    <div className="entry-tag-list">
                      {entry.tags.map((tag) => (
                        <button
                          key={`${entry.id}-${tag}`}
                          type="button"
                          className={`entry-tag-chip ${entrySearch.trim().toLowerCase() === tag.toLowerCase() ? "is-active" : ""}`}
                          onClick={() => setEntrySearch(tag)}
                          title={`Filter by ${tag}`}
                        >
                          #{tag}
                        </button>
                      ))}
                    </div>
                  ) : null}
                  <p className="group-entry-preview">{getEntryPreview(entry.note)}</p>
                </div>
                <div className="note-card-actions">
                  {canOpenFolder ? (
                    <button
                      className="action-link"
                      onClick={() => openFolder(openFolderPath)}
                    >
                      Open
                    </button>
                  ) : null}
                  <button
                    className="action-link"
                    onClick={() => handleToggleExpand(entry.id)}
                  >
                    {expandedEntryId === entry.id ? "Collapse" : "Expand"}
                  </button>
                  <button className="action-link" onClick={() => handleCopyNote(entry)}>
                    {copiedEntryId === entry.id ? "Copied" : "Copy"}
                  </button>
                  {expandedEntryId === entry.id ? (
                    <button
                      className="action-link"
                      onClick={() => handleToggleHistory(entry.id)}
                    >
                      {expandedHistoryEntryId === entry.id ? "Hide history" : "History"}
                    </button>
                  ) : null}
                  <button className="action-link" onClick={() => onEditEntry(entry, group)}>Edit</button>
                  <button
                    className="action-link"
                    onClick={() => {
                      setMovingFolderPath("");
                      setMovingEntryId((current) => (current === entry.id ? "" : entry.id));
                      setMoveTargetGroupId("");
                      setMoveTargetFolderPath(entry.folder_path || "");
                      setMoveFolderOptions([]);
                    }}
                  >
                    Move
                  </button>
                  <button className="action-link danger" onClick={() => handleDeleteEntry(entry.id)}>Delete</button>
                </div>
              </div>
              {movingEntryId === entry.id ? (
                <div className="group-move-form">
                  <label className="editor-field">
                    <span>Move to another title</span>
                    <select
                      value={moveTargetGroupId}
                      onChange={async (event) => {
                        const nextGroupId = event.target.value;
                        setMoveTargetGroupId(nextGroupId);
                        setMoveTargetFolderPath("");
                        await loadMoveFolders(nextGroupId, entry.id, "");
                      }}
                    >
                      <option value="">Choose destination title</option>
                      {groupOptions.map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.id === groupId ? `${option.title} (current title)` : option.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  {moveTargetGroupId ? (
                    <>
                      <label className="editor-field">
                        <span>Current folder location</span>
                        <select
                          value={moveTargetFolderPath}
                          onChange={(event) => setMoveTargetFolderPath(event.target.value)}
                          disabled={loadingMoveFolders}
                        >
                          <option value="">
                            {loadingMoveFolders ? "Loading folders..." : "Top level of destination title"}
                          </option>
                          {moveFolderOptions.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </label>
                      <div className="editor-field">
                        <span>Folder creation rules</span>
                        <div className="folder-picker-card">
                          <strong>{moveTargetFolderPath || "Top level of destination title"}</strong>
                          <p>
                            {!moveTargetFolderPath
                              ? "Create a root folder here, or move the note to the top level."
                              : "You are inside this destination folder. New folders can only be created under this exact location."}
                          </p>
                          <div className="folder-picker-actions">
                            <button
                              type="button"
                              className="action-button action-button-secondary"
                              onClick={handleCreateMoveFolder}
                              disabled={loadingMoveFolders}
                            >
                              {moveTargetFolderPath ? "Create sub-folder here" : "Create root folder"}
                            </button>
                            {moveTargetFolderPath ? (
                              <button
                                type="button"
                                className="action-link"
                                onClick={() => setMoveTargetFolderPath("")}
                                disabled={loadingMoveFolders}
                              >
                                Return to top level
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : null}
                  <div className="group-move-actions">
                    <button
                      className="action-button action-button-secondary"
                      onClick={() => handleMoveEntry(entry.id)}
                      disabled={moving || !moveTargetGroupId}
                    >
                      {moving ? "Moving..." : "Confirm move"}
                    </button>
                    <button
                      className="action-link"
                      onClick={() => {
                        setMovingEntryId("");
                        setMoveTargetGroupId("");
                        setMoveTargetFolderPath("");
                        setMoveFolderOptions([]);
                      }}
                      disabled={moving}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : null}
              {expandedEntryId === entry.id ? (
                <>
                  <div className="group-entry-body">
                    {entry.id === "d85e9b6b-ddd3-489f-9436-ebe93feb499c" ? <LabStatusRefreshButton entryId={entry.id} onUpdated={updated=>setEntries(current=>current.map(item=>item.id===updated.id?{...item,...updated}:item))} /> : null}
                    {entry.note?.includes("study-guide-slides")
                      ? <StudyGuideSlideDeck note={entry.note} title={entry.title} />
                      : entry.note?.includes("generated-config-note")
                      ? renderGeneratedConfigNote(entry)
                      : <div className="note-rendered-content" dangerouslySetInnerHTML={renderEntryHTML(entry.note)} />}
                  </div>
                  {expandedHistoryEntryId === entry.id ? (
                    <div className="entry-history-panel">
                      <strong>History</strong>
                      {loadingVersionsFor === entry.id ? <span>Loading history...</span> : null}
                      {loadingVersionsFor !== entry.id && !(versionsByEntry[entry.id] || []).length ? (
                        <span>No saved history yet.</span>
                      ) : null}
                      {(versionsByEntry[entry.id] || []).slice(0, 6).map((version) => (
                        <div key={version.id} className="entry-history-row">
                          <span>{formatDate(version.created_at)} · {version.status}</span>
                          <button className="action-link" onClick={() => handleRestoreVersion(entry.id, version.id)}>
                            Restore
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </>
              ) : null}
            </li>
              );
            })}
          </ol>
        </>
      )}
    </section>
  );
}




